import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, Button, Divider, Drawer, IconButton, Tooltip, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {toast} from 'react-toastify';
import * as STYLE_CONSTANTS from '../../styles/styleConstants';
import {
  getAiCallNotificationPermission,
  requestAiCallNotificationPermission,
  showTestCallNotification,
} from '../../utils/aiCallNotifications';

const {COLORS} = STYLE_CONSTANTS;

/**
 * The browser's notification permission, kept current: the user can change it from the address
 * bar at any time, and the bell has to say so.
 */
function useNotificationPermission() {
  const [permission, setPermission] = useState(getAiCallNotificationPermission);

  useEffect(() => {
    const refresh = () => setPermission(getAiCallNotificationPermission());
    let status = null;
    navigator.permissions?.query({name: 'notifications'})
      .then((result) => {
        status = result;
        status.onchange = refresh;
      })
      .catch(() => undefined);
    // Not every browser reports changes; coming back to the tab is the next best moment to look.
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      if (status) {
        status.onchange = null;
      }
    };
  }, []);

  return [permission, setPermission];
}

/**
 * Top of the drawer for an AI-call agent: whether incoming calls will reach them as desktop
 * notifications, and the click that turns them on. Browsers only ask for notification permission
 * in response to a click, which is why this lives behind the bell rather than running on load.
 */
const DesktopAlertsStatus = ({permission, setPermission}) => {
  const turnOn = useCallback(async () => {
    const result = await requestAiCallNotificationPermission();
    setPermission(result);
    if (result === 'granted') {
      toast('Desktop call alerts are on.', {type: 'success'});
    }
  }, [setPermission]);

  const sendTest = useCallback(() => {
    showTestCallNotification();
    toast(
      'A test alert is on its way. If it does not appear, check that Focus Assist / Do Not '
        + 'Disturb is off and that your browser is allowed to show notifications.',
      {type: 'info', toastId: 'ai-call-alerts-test'}
    );
  }, []);

  if (permission === 'unsupported') {
    return null;
  }

  const granted = permission === 'granted';
  const denied = permission === 'denied';

  return (
    <Box
      sx={{
        mx: 2,
        my: 1.5,
        p: 1.5,
        borderRadius: '6px',
        backgroundColor: granted ? COLORS.lightGreen : COLORS.globalRedPale,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {granted
          ? <NotificationsActiveIcon sx={{color: COLORS.globalGreen}} fontSize="small" />
          : <NotificationsOffIcon sx={{color: COLORS.globalRed}} fontSize="small" />}
        <Typography sx={{fontWeight: 700, fontSize: '0.9rem', flexGrow: 1}}>
          {granted ? 'Desktop call alerts are on' : 'Desktop call alerts are off'}
        </Typography>
        {granted && (
          <Button size="small" onClick={sendTest} sx={{textTransform: 'none', minWidth: 0}}>
            Send test
          </Button>
        )}
        {!granted && !denied && (
          <Button
            size="small"
            variant="contained"
            onClick={turnOn}
            sx={{textTransform: 'none', backgroundColor: COLORS.globalBlue}}
          >
            Turn on
          </Button>
        )}
      </Box>
      <Typography sx={{fontSize: '0.8rem', color: COLORS.fontPale, mt: 0.5}}>
        {granted && 'Incoming AI calls show as a Windows / macOS alert while you are Available.'}
        {!granted && !denied
          && 'Turn them on to see incoming AI calls even when iPermit is behind other windows.'}
        {denied
          && 'Blocked in this browser. Click the icon left of the address bar, allow Notifications '
            + 'for this site, then reload the page.'}
      </Typography>
    </Box>
  );
};

/**
 * Navbar bell: opens the notifications drawer. For an AI-call agent it is also coloured by the
 * browser's notification permission (green = desktop call alerts allowed, red = not), which the
 * top of the drawer explains and lets them change.
 *
 * The list itself is empty until the notifications backend exists (ADO #4400).
 */
const NotificationsBell = () => {
  const user = useSelector((state) => state.user);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useNotificationPermission();

  const showsCallAlerts = Boolean(user?.acceptAiCall) && permission !== 'unsupported';
  const granted = permission === 'granted';

  let iconColor = COLORS.fontMain;
  let title = 'Notifications';
  if (showsCallAlerts) {
    iconColor = granted ? COLORS.globalGreen : COLORS.globalRed;
    title = granted
      ? 'Notifications. Desktop call alerts are on.'
      : 'Notifications. Desktop call alerts are off: open to turn them on.';
  }

  let icon = <NotificationsNoneIcon />;
  if (showsCallAlerts) {
    icon = granted ? <NotificationsActiveIcon /> : <NotificationsOffIcon />;
  }

  return (
    <>
      <Tooltip title={title}>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="Open notifications"
          sx={{ml: 0.5, flexShrink: 0, color: iconColor}}
        >
          {icon}
        </IconButton>
      </Tooltip>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{width: {xs: '100vw', sm: 380}}} role="presentation">
          <Box display="flex" alignItems="center" px={2} py={1.5}>
            <Typography sx={{fontWeight: 700, fontSize: '1.1rem', flexGrow: 1}}>
              Notifications
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close notifications">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          {showsCallAlerts && (
            <DesktopAlertsStatus permission={permission} setPermission={setPermission} />
          )}
          <Box px={2} py={4} textAlign="center">
            <NotificationsNoneIcon sx={{color: COLORS.fontPale, fontSize: 40}} />
            <Typography sx={{color: COLORS.fontPale, mt: 1}}>No notifications yet</Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default NotificationsBell;
